import { type RunBox, layoutRichText } from './textLayout';
import {
  type BorderStyle,
  type CellStyle,
  type CellVerticalAlign,
  HIDDEN_CELL_STYLE,
  type RenderedCell,
} from '@/model/cellStyle';
import { RectRange } from '@/model/range';
import { RichText } from '@/model/richText';
import type { Sheet } from '@/model/sheet';
import { Vector2 } from '@/utils/vec';
import { Matrix, PDF, PDFPage, ops, rgb } from '@libpdf/core';

type Operator = ReturnType<(typeof ops)['pushGraphicsState']>;

// TODO: Move to a utils file
// This exists because Matrix.prototype.translate translates in relative coordinates
function translate(matrix: Matrix, x: number, y: number) {
  return new Matrix(
    matrix.a,
    matrix.b,
    matrix.c,
    matrix.d,
    matrix.e + x,
    matrix.f + y,
  );
}

export type PrintSettings = {
  pageSize: Vector2;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  // TODO: stretch vertically too eventually (two separate options)
  // TODO: Allow for stretch, but also split into multiple pages (right now, if stretch is enabled, it disables horizontal splitting)
  stretchToFit?: boolean;
};

// idk how I feel about this being a generator
function* printRichTextRun({
  box,
  pos,
  align,
  totalHeight,
  page,
}: {
  box: RunBox;
  pos: Vector2;
  align: CellVerticalAlign;
  totalHeight: number;
  page: PDFPage;
}): Iterable<
  Parameters<PDFPage['drawText']> | Parameters<PDFPage['drawRectangle']>[0]
> {
  // Pos is top-left
  // drawText is bottom-left
  const { word, font, height, width, style, lastBox, whitespace, textHeight } =
    box;
  console.log({ word, font });
  // TODO: strikethrough, underline
  const { size, color, background, strikethrough, underline } = style;

  if (lastBox && whitespace) {
    return;
  }

  pos = pos.sub([0, totalHeight]);

  if (background) {
    const bg = background.toCo();
    if (bg.opacity > 0) {
      // FIXME: doesn't take into account font descender
      yield {
        x: pos.x,
        y: pos.y,
        width,
        height,
        ...bg,
      };
    }
  }

  pos = pos.add([0, height - textHeight]);

  yield [
    word,
    {
      x: pos.x,
      y: pos.y,
      size,
      font,
      ...color.toCo(),
    },
  ];
}

async function printRichText({
  richText,
  pos,
  size,
  style,
  pdf,
  page,
}: {
  richText: RichText;
  pos: Vector2;
  size: Vector2;
  style: CellStyle;
  pdf: PDF;
  page: PDFPage;
}) {
  const clipBl = pos.sub([0, size.y]);
  const clipTr = pos.add([size.x, 0]);
  const { padding, justify, verticalAlign, textWrap, lineHeight } = style;
  pos = pos.add([padding.x, -padding.y]);

  page.drawOperators([
    ops.pushGraphicsState(),
    ops.moveTo(clipBl.x, clipBl.y),
    ops.lineTo(clipBl.x, clipTr.y),
    ops.lineTo(clipTr.x, clipTr.y),
    ops.lineTo(clipTr.x, clipBl.y),
    ops.clip(),
    ops.endPath(),
  ]);
  page.drawOperators([ops.concatMatrix(Matrix.translate(pos.x, pos.y))]);

  let currentPos = new Vector2(0, 0);

  const lines = await layoutRichText({
    cellStyle: style,
    richText,
    pdf,
    settings: {
      width: size.x - padding.x * 2,
      height: size.y - padding.y * 2,
      justify,
      verticalAlign,
      textWrap,
    },
  });

  if (verticalAlign === 'middle') {
    currentPos = currentPos.sub([
      0,
      (size.y - lines.height * lineHeight) / 2 - padding.y,
    ]);
  } else if (verticalAlign === 'bottom') {
    currentPos = currentPos.sub([
      0,
      size.y -
        lines.heightWithoutLastLine * lineHeight -
        lines.lastLineHeight -
        padding.y * 2,
    ]);
  }

  const bgCalls: Parameters<PDFPage['drawRectangle']>[0][] = [];
  const textCalls: Parameters<PDFPage['drawText']>[] = [];

  for (const line of lines.lines) {
    if (justify === 'center') {
      currentPos = currentPos.add([(size.x - line.width) / 2 - padding.x, 0]);
    } else if (justify === 'right') {
      currentPos = currentPos.add([size.x - line.width - padding.x * 2, 0]);
    }
    for (const run of line.runs) {
      for (const call of printRichTextRun({
        box: run,
        pos: currentPos,
        align: style.verticalAlign,
        totalHeight: line.height,
        page,
      })) {
        if (Array.isArray(call)) {
          textCalls.push(call);
        } else {
          bgCalls.push(call);
        }
      }
      currentPos = currentPos.add([run.width, 0]);
    }
    currentPos = new Vector2(0, currentPos.y - line.height * lineHeight);
  }

  for (const call of bgCalls) {
    page.drawRectangle(call);
  }

  for (const call of textCalls) {
    page.drawText(...call);
  }

  page.drawOperators([ops.popGraphicsState()]);
}

function printBorder({
  start,
  end,
  style,
  page,
}: {
  start: Vector2;
  end: Vector2;
  style: BorderStyle;
  page: PDFPage;
}) {
  const { width, color, style: borderStyle } = style;
  const c = color.toCo();
  if (width <= 0 || c.opacity <= 0) return;
  let dashArray: number[] | undefined;

  if (borderStyle === 'dashed') {
    dashArray = [width * 2, width * 2];
  } else if (borderStyle === 'dotted') {
    dashArray = [0, width * 2];
  }

  page.drawLine({
    start,
    end,
    ...c,
    thickness: width,
    dashArray,
    lineCap: borderStyle === 'dotted' ? 'round' : 'butt',
  });
}

async function printCellOnPage({
  cell,
  pos,
  size,
  printSettings,
  pdf,
  page,
}: {
  cell: RenderedCell;
  pos: Vector2;
  size: Vector2;
  printSettings: PrintSettings;
  pdf: PDF;
  page: PDFPage;
}) {
  const { pageSize } = printSettings;
  pos = new Vector2(pos.x, pageSize.y - pos.y);

  const tl = pos;
  const tr = pos.add([size.x, 0]);
  const bl = pos.sub([0, size.y]);

  const { borderTop, borderLeft, background } = cell.style;

  const bgColor = background.toCo();
  if (bgColor.opacity > 0) {
    page.drawRectangle({
      x: pos.x,
      y: pos.y - size.y,
      width: size.x,
      height: size.y,
      ...bgColor,
    });
  }

  printBorder({
    start: tl,
    end: tr,
    style: borderTop,
    page,
  });

  printBorder({
    start: tl,
    end: bl,
    style: borderLeft,
    page,
  });

  if (cell.value) {
    await printRichText({
      richText: RichText.of(cell.value),
      pos,
      size,
      style: cell.style,
      pdf,
      page,
    });
  }
}

/**
 * Assumes range already fits h and v on the page.
 */
async function printRangeOnPage({
  sheet,
  pos,
  size,
  range,
  printSettings,
  pdf,
  page,
}: {
  sheet: Sheet;
  pos: Vector2;
  size?: Vector2;
  range: RectRange;
  printSettings: PrintSettings;
  pdf: PDF;
  page: PDFPage;
}) {
  if (range.isInfinite()) {
    throw new Error('Cannot print infinite range');
  }

  let hStretch = 1;

  if (printSettings.stretchToFit && size) {
    const rangeWidth = range.countWidth(sheet);
    const pageWidth = size.x;
    hStretch = pageWidth / rangeWidth;
  }

  const cells = range.getRenderedCells(sheet, HIDDEN_CELL_STYLE);
  let maxPos = new Vector2(0, 0);
  for (const cell of cells) {
    await printCellOnPage({
      ...cell,
      pos: cell.pos.mult([hStretch, 1]).add(pos),
      size: cell.size.mult([hStretch, 1]),
      printSettings,
      pdf,
      page,
    });
    maxPos = maxPos.max(cell.pos.add(cell.size));
  }
  // TODO: make a better way to render ends of the range
  const below = range.downOfRange();
  const belowCells = below.getRenderedCells(sheet, HIDDEN_CELL_STYLE);
  const right = range.rightOfRange();
  const rightCells = right.getRenderedCells(sheet, HIDDEN_CELL_STYLE);
  for (const cell of belowCells) {
    await printCellOnPage({
      ...cell,
      pos: cell.pos.mult([hStretch, 1]).add(pos).add([0, maxPos.y]),
      size: cell.size.mult([hStretch, 1]),
      cell: {
        cell: undefined,
        table: undefined,
        value: '',
        pos: cell.cell.pos,
        style: {
          ...HIDDEN_CELL_STYLE,
          borderTop: cell.cell.style.borderTop,
        },
      },
      printSettings,
      pdf,
      page,
    });
  }
  for (const cell of rightCells) {
    await printCellOnPage({
      ...cell,
      pos: cell.pos
        .mult([hStretch, 1])
        .add(pos)
        .add([maxPos.x * hStretch, 0]),
      size: cell.size.mult([hStretch, 1]),
      cell: {
        cell: undefined,
        table: undefined,
        value: '',
        pos: cell.cell.pos,
        style: {
          ...HIDDEN_CELL_STYLE,
          borderLeft: cell.cell.style.borderLeft,
        },
      },
      printSettings,
      pdf,
      page,
    });
  }
}

/**
 * Assumes range already fits horizontally on the page.
 */
async function printRangeVerticalPass({
  sheet,
  range,
  printSettings,
  pdf,
  page,
}: {
  sheet: Sheet;
  range: RectRange;
  printSettings: PrintSettings;
  pdf: PDF;
  page: PDFPage;
}): Promise<RectRange | undefined> {
  const { marginTop, marginBottom, marginLeft, marginRight, pageSize } =
    printSettings;

  // At least 1 to not enter an infinite loop
  const rowCount = Math.max(
    range.countRows(sheet, pageSize.y - marginTop - marginBottom),
    1,
  );

  const pageRange = range.newWithSize([range.width, rowCount]);

  // TODO: since nothing else depends on this, it should be awaited further up
  await printRangeOnPage({
    sheet,
    pos: new Vector2(0, 0),
    size: pageSize.sub([marginLeft + marginRight, marginTop + marginBottom]),
    range: pageRange,
    printSettings,
    pdf,
    page,
  });

  if (rowCount >= range.height) {
    return undefined;
  }

  return new RectRange(range.min.max(0).add([0, rowCount]), range.max);
}

async function printRangeHorizontalPass({
  sheet,
  range,
  printSettings,
  pdf,
}: {
  sheet: Sheet;
  range: RectRange;
  printSettings: PrintSettings;
  pdf: PDF;
}): Promise<{ newPages: PDFPage[]; newRange: RectRange | undefined }> {
  const {
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    pageSize,
    stretchToFit,
  } = printSettings;

  let colCount;
  let pageRange;

  if (stretchToFit && !range.isRowRange()) {
    colCount = range.width;
    pageRange = range;
  } else {
    colCount = range.countCols(sheet, pageSize.x - marginLeft - marginRight);
    pageRange = range.newWithSize([colCount, range.height]);
  }
  const pages: PDFPage[] = [];

  let activeRange: RectRange | undefined = pageRange;
  while (activeRange) {
    const operators = [
      ops.pushGraphicsState(),
      ops.concatMatrix(Matrix.translate(marginLeft, -marginTop)),
    ];

    let page = pdf.addPage(printSettings.pageSize.wh);

    const { appendOperators: ogAppendOperators } = page as unknown as {
      appendOperators: (ops: Operator[]) => void;
    };
    Object.assign(page, {
      appendOperators: (ops: Operator[]) => operators.push(...ops),
    });

    pages.push(page);
    activeRange = await printRangeVerticalPass({
      sheet,
      range: activeRange,
      printSettings,
      pdf,
      page,
    });

    operators.push(ops.popGraphicsState());

    ogAppendOperators.call(page, operators);

    Object.assign(page, {
      appendOperators: ogAppendOperators,
    });

    if (activeRange && activeRange.isInfinite() && !activeRange.hasCells(sheet))
      break;
  }

  if (colCount >= range.width) {
    return {
      newPages: pages,
      newRange: undefined,
    };
  }

  return {
    newPages: pages,
    newRange: new RectRange(range.min.max(0).add([colCount, 0]), range.max),
  };
}

function printMargins({
  page,
  printSettings,
}: {
  page: PDFPage;
  printSettings: PrintSettings;
}) {
  const { marginTop, marginBottom, marginLeft, marginRight, pageSize } =
    printSettings;

  const color = rgb(255, 0, 255);

  page.drawLine({
    start: new Vector2(marginLeft, pageSize.y - marginBottom),
    end: new Vector2(marginLeft, marginTop),
    color,
  });
  page.drawLine({
    start: new Vector2(pageSize.x - marginRight, pageSize.y - marginBottom),
    end: new Vector2(pageSize.x - marginRight, marginTop),
    color,
  });
  page.drawLine({
    start: new Vector2(marginLeft, pageSize.y - marginBottom),
    end: new Vector2(pageSize.x - marginRight, pageSize.y - marginBottom),
    color,
  });
  page.drawLine({
    start: new Vector2(marginLeft, marginTop),
    end: new Vector2(pageSize.x - marginRight, marginTop),
    color,
  });
}

async function printRange({
  pdf,
  sheet,
  range,
  printSettings,
}: {
  pdf: PDF;
  sheet: Sheet;
  range: RectRange;
  printSettings: PrintSettings;
}): Promise<PDFPage[]> {
  const pages: PDFPage[] = [];

  let activeRange: RectRange | undefined = range;

  do {
    const { newPages, newRange } = await printRangeHorizontalPass({
      sheet,
      range: activeRange,
      printSettings,
      pdf,
    });
    pages.push(...newPages);
    activeRange = newRange;
  } while (
    activeRange &&
    (!activeRange.isInfinite() || activeRange.hasCells(sheet))
  );

  return pages;
}

export async function printSheet({
  sheet,
  range,
  printSettings,
}: {
  sheet: Sheet;
  range?: RectRange;
  printSettings: PrintSettings;
}): Promise<Uint8Array> {
  const pdf = PDF.create();

  pdf.setCreator('GridForm');

  let headerRange: RectRange | undefined;
  let footerRange: RectRange | undefined;

  let mainStf = printSettings.stretchToFit;
  let headerStf = printSettings.stretchToFit;
  let footerStf = printSettings.stretchToFit;

  if (!range) {
    const { mainTable, headerTable, footerTable } = sheet;
    if (!mainTable) throw new Error('No main table');
    range = mainTable.range;
    if (mainTable.style.stretchToFit) mainStf = true;

    if (headerTable) {
      headerRange = headerTable.range;
      if (headerTable.style.stretchToFit) headerStf = true;
    }
    if (footerTable) {
      footerRange = footerTable.range;
      if (footerTable.style.stretchToFit) footerStf = true;
    }

    if (headerRange?.isInfinite())
      throw new Error('Cannot print infinite header range');
    if (footerRange?.isInfinite())
      throw new Error('Cannot print infinite footer range');
  }

  const headerHeight = headerRange?.countHeight(sheet) ?? 0;
  const footerHeight = footerRange?.countHeight(sheet) ?? 0;

  const pages: PDFPage[] = await printRange({
    sheet,
    range,
    printSettings: {
      ...printSettings,
      marginTop: printSettings.marginTop + headerHeight,
      marginBottom: printSettings.marginBottom + footerHeight,
      stretchToFit: mainStf,
    },
    pdf,
  });

  const pageSize = printSettings.pageSize.sub([
    printSettings.marginLeft + printSettings.marginRight,
    printSettings.marginTop + printSettings.marginBottom,
  ]);

  for (const page of pages) {
    // printHeader({ page, pageCount: pages.length, printSettings });
    // printMargins({ page, printSettings });
    if (headerRange) {
      printRangeOnPage({
        sheet,
        pos: new Vector2(printSettings.marginLeft, printSettings.marginTop),
        size: pageSize,
        range: headerRange,
        printSettings: {
          ...printSettings,
          stretchToFit: headerStf,
        },
        pdf,
        page,
      });
    }
    if (footerRange) {
      printRangeOnPage({
        sheet,
        pos: new Vector2(
          printSettings.marginLeft,
          printSettings.pageSize.y - printSettings.marginBottom - footerHeight,
        ),
        size: pageSize,
        printSettings: {
          ...printSettings,
          stretchToFit: footerStf,
        },
        range: footerRange,
        pdf,
        page,
      });
    }
  }

  return pdf.save();
}
